import React from 'react';
import { Check, Download } from '../../shared/components/Icons';
import { downloadExport } from '../../shared/utils/api';
import { formatDate } from '../../shared/utils/dateUtils';

export default function DataSettings({
  activeTab,
  setShowImport,
  // billing props
  user, openPortal, portalLoading, portalError,
  isLifetime, isMonthly, withinRefundWindow,
  refundDone, showRefundForm, setShowRefundForm,
  refundReason, setRefundReason, refundWentWrong, setRefundWentWrong,
  refundImprovement, setRefundImprovement,
  refundSubmitting, refundError, submitRefund,
}) {
  if (activeTab === 'data') {
    return (
      <>
        <div className="settings-section">
          <div className="settings-section-title">Data</div>
          <div className="settings-row">
            <div className="settings-row-left">
              <span className="settings-row-label">Export your data</span>
              <span className="settings-row-key">Download all topics and MCQ history as Excel</span>
            </div>
            <div className="settings-row-right">
              <button className="settings-action-btn" onClick={downloadExport}>
                <Download size={13} /> Export to Excel
              </button>
            </div>
          </div>
          <div className="settings-row">
            <div className="settings-row-left">
              <span className="settings-row-label">Import data</span>
              <span className="settings-row-key">Import confidence ratings from a spreadsheet</span>
            </div>
            <div className="settings-row-right">
              <button className="settings-action-btn" onClick={() => setShowImport(true)}>
                Import spreadsheet
              </button>
            </div>
          </div>
        </div>

        <div className="settings-section">
          <div className="settings-section-title">Testing</div>
          <div className="settings-info-row">
            <span className="settings-info-label">New user preview</span>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              <button className="settings-newuser-btn"
                onClick={async () => {
                  const { api } = await import('../../shared/utils/api');
                  await api('/api/debug/reset-new-user', { method: 'POST' });
                  window.location.reload();
                }}>
                Preview as new user
              </button>
              <span style={{ fontSize: 11, color: 'var(--hint)' }}>Resets onboarding flags only. All data is kept.</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (activeTab === 'billing') {
    return (
      <>
        <div className="settings-section">
          <div className="settings-section-title">Your plan</div>
          <div className="settings-row">
            <div className="settings-row-left">
              <span className="settings-row-label">Current plan</span>
            </div>
            <div className="settings-row-right">
              <span className="settings-muted-val">
                {user.tier === 'monthly' ? 'Monthly — £9.99/mo' :
                 (user.tier === 'tracker_practice' || user.tier === 'complete') ? 'Tracker + Practice — one-time' :
                 user.tier || 'Unknown'}
              </span>
            </div>
          </div>
          {user.paid_at && (
            <div className="settings-row">
              <div className="settings-row-left">
                <span className="settings-row-label">Purchased</span>
              </div>
              <div className="settings-row-right">
                <span className="settings-muted-val">{formatDate(user.paid_at, 'full')}</span>
              </div>
            </div>
          )}
        </div>

        {isMonthly && (
          <div className="settings-section">
            <div className="settings-section-title">Subscription</div>
            <div className="settings-row">
              <div className="settings-row-left">
                <span className="settings-row-label">Manage subscription</span>
                <span className="settings-row-key">Update payment method, cancel, or view invoices</span>
              </div>
              <div className="settings-row-right">
                <button className="settings-action-btn" onClick={openPortal} disabled={portalLoading}>
                  {portalLoading ? 'Opening' : 'Manage subscription'}
                </button>
              </div>
            </div>
            {portalError && <div className="settings-error" style={{ padding: '8px 16px' }}>{portalError}</div>}
          </div>
        )}

        {isLifetime && (
          <div className="settings-section">
            <div className="settings-section-title">Refund</div>
            {refundDone ? (
              <div style={{ padding: 'var(--sp-3) var(--sp-4)', color: 'var(--green)', fontSize: 13, fontWeight: 500 }}>
                <Check size={13} style={{ marginRight: 4 }} /> Refund request submitted. We will review it within 1-2 business days.
              </div>
            ) : withinRefundWindow ? (
              <div style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
                <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.5 }}>
                  You are within the 14-day refund window. If SQETrack is not working for you, we will refund your purchase in full.
                </p>
                {!showRefundForm ? (
                  <button className="settings-action-btn" onClick={() => setShowRefundForm(true)}>
                    Request a refund
                  </button>
                ) : (
                  <form onSubmit={submitRefund} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                      <label className="settings-row-label" style={{ display: 'block', marginBottom: 4 }}>What is the main reason for your refund request?</label>
                      <textarea className="settings-text-input" rows={2} value={refundReason}
                        onChange={e => setRefundReason(e.target.value)} required
                        style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }} />
                      {refundReason.length > 0 && refundReason.length < 20 && (
                        <span style={{ fontSize: 11, color: 'var(--amber)' }}>At least 20 characters needed so we can understand your experience.</span>
                      )}
                    </div>
                    <div>
                      <label className="settings-row-label" style={{ display: 'block', marginBottom: 4 }}>What was not working for you?</label>
                      <textarea className="settings-text-input" rows={2} value={refundWentWrong}
                        onChange={e => setRefundWentWrong(e.target.value)}
                        style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }} />
                    </div>
                    <div>
                      <label className="settings-row-label" style={{ display: 'block', marginBottom: 4 }}>Is there anything we could have done better?</label>
                      <textarea className="settings-text-input" rows={2} value={refundImprovement}
                        onChange={e => setRefundImprovement(e.target.value)}
                        style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }} />
                      {refundImprovement.length > 0 && refundImprovement.length < 20 && (
                        <span style={{ fontSize: 11, color: 'var(--amber)' }}>At least 20 characters needed so we can understand your experience.</span>
                      )}
                    </div>
                    {refundError && <div className="settings-error">{refundError}</div>}
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="settings-action-btn" type="button" onClick={() => setShowRefundForm(false)}>Cancel</button>
                      <button className="settings-save-btn" type="submit" disabled={refundSubmitting || !refundReason.trim() || (refundReason.length < 20) || (refundImprovement.length > 0 && refundImprovement.length < 20)}>
                        {refundSubmitting ? 'Submitting' : 'Submit refund request'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            ) : (
              <div style={{ padding: 'var(--sp-3) var(--sp-4)' }}>
                <p style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.5 }}>
                  The 14-day refund window has passed. If you have concerns, email <a href="mailto:hello@sqetrack.co.uk">hello@sqetrack.co.uk</a>.
                </p>
              </div>
            )}
          </div>
        )}
      </>
    );
  }

  return null;
}
